import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { MagnifyingGlass, Check, UsersThree } from '@phosphor-icons/react';
import useDirectory from '@/hooks/useDirectory';
import { itApi } from './itApi';

const EMPTY = {
  email: '', purpose: '', owner_employee_ids: [],
  password: '', aliases: '', status: 'active', notes: '',
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs font-semibold text-zinc-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function EmailFormDialog({ open, onClose, account, onSaved }) {
  const editing = !!account;
  const { employees } = useDirectory();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [ownerQuery, setOwnerQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setOwnerQuery('');
    if (account) {
      const ids = account.owner_employee_ids
        || (account.owner_employee_id ? [account.owner_employee_id] : []);
      setForm({
        email: account.email || '', purpose: account.purpose || '',
        owner_employee_ids: ids, password: '',
        aliases: account.aliases || '', status: account.status || 'active',
        notes: account.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, account]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const toggleOwner = (id) => setForm((p) => ({
    ...p,
    owner_employee_ids: p.owner_employee_ids.includes(id)
      ? p.owner_employee_ids.filter((x) => x !== id)
      : [...p.owner_employee_ids, id],
  }));

  const filteredEmps = useMemo(() => {
    const q = ownerQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => (e.name || '').toLowerCase().includes(q) || String(e.code || '').includes(q));
  }, [employees, ownerQuery]);

  const selectedNames = form.owner_employee_ids
    .map((id) => employees.find((e) => e.id === id)?.name)
    .filter(Boolean);

  const submit = async () => {
    if (!form.email.trim()) { toast.error('メールアドレスを入力してください'); return; }
    setSaving(true);
    const payload = {
      email: form.email.trim(),
      purpose: form.purpose || null,
      owner_employee_ids: form.owner_employee_ids,
      aliases: form.aliases || null,
      status: form.status,
      notes: form.notes || null,
    };
    if (form.password) payload.password = form.password;
    try {
      if (editing) await itApi.updateEmail(account.id, payload);
      else await itApi.createEmail(payload);
      toast.success(editing ? 'メールアカウントを更新しました' : 'メールアカウントを登録しました');
      onSaved?.(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="email-form-dialog">
        <DialogHeader><DialogTitle>{editing ? 'メールアカウントを編集' : 'メールアカウントを登録'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          {/* Step 1 — pick the person(s) first */}
          <Field label="① 利用者（社員）— このメールを使う人" testid="email-field-owners">
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <div className="relative border-b border-zinc-200 bg-zinc-50/50">
                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="email-owner-search" value={ownerQuery}
                  onChange={(e) => setOwnerQuery(e.target.value)}
                  placeholder="名前・社員番号で検索"
                  className="w-full h-9 pl-9 pr-3 text-sm bg-transparent outline-none"
                />
              </div>
              <div className="max-h-44 overflow-y-auto" data-testid="email-owner-list">
                {filteredEmps.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-4">該当する社員がいません</p>
                ) : filteredEmps.map((e) => {
                  const sel = form.owner_employee_ids.includes(e.id);
                  return (
                    <button
                      key={e.id} type="button" data-testid={`email-owner-opt-${e.id}`}
                      onClick={() => toggleOwner(e.id)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${sel ? 'bg-emerald-50' : 'hover:bg-zinc-50'}`}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded border shrink-0 ${sel ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-zinc-300'}`}>
                        {sel && <Check size={11} weight="bold" />}
                      </span>
                      <span className="text-zinc-800 truncate">{e.name}</span>
                      {e.code && <span className="text-[11px] text-zinc-400 ml-auto font-mono">{e.code}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedNames.length > 0 && (
              <p className="text-[11px] text-emerald-600 mt-1.5 flex items-center gap-1" data-testid="email-owner-selected">
                <UsersThree size={13} weight="duotone" /> 選択中: {selectedNames.join('、')}（{selectedNames.length}名）
              </p>
            )}
            <p className="text-[11px] text-zinc-400 mt-1">複数人を選択できます（共有メールの場合は選択なしでも可）。</p>
          </Field>

          {/* Step 2 — the email + credentials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="② メールアドレス *" testid="email-field-address">
                <Input data-testid="email-input-address" value={form.email} onChange={set('email')} placeholder="info@sensbey.co.jp" className="h-10" />
              </Field>
            </div>
            <Field label={editing ? 'パスワード（空欄=変更なし）' : 'パスワード'} testid="email-field-password">
              <Input data-testid="email-input-password" type="text" autoComplete="new-password" value={form.password} onChange={set('password')} placeholder={editing ? '••••••（変更時のみ）' : ''} className="h-10 font-mono" />
            </Field>
            <Field label="ステータス" testid="email-field-status">
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger data-testid="email-input-status" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">有効</SelectItem>
                  <SelectItem value="suspended">停止</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="用途" testid="email-field-purpose">
              <Input data-testid="email-input-purpose" value={form.purpose} onChange={set('purpose')} placeholder="例: 問い合わせ窓口" className="h-10" />
            </Field>
            <Field label="エイリアス（カンマ区切り）" testid="email-field-aliases">
              <Input data-testid="email-input-aliases" value={form.aliases} onChange={set('aliases')} placeholder="contact@..., sales@..." className="h-10" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="備考" testid="email-field-notes">
                <Textarea data-testid="email-input-notes" value={form.notes} onChange={set('notes')} className="min-h-[60px]" />
              </Field>
            </div>
          </div>
          <p className="text-[11px] text-zinc-400">※ パスワードは暗号化して保存され、一覧には表示されません（表示操作は監査ログに記録されます）。</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="email-form-cancel">キャンセル</Button>
          <Button data-testid="email-form-submit" disabled={saving} onClick={submit} className="bg-teal-700 hover:bg-teal-800">
            {editing ? '更新する' : '登録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
